import React from 'react';
import { Tooltip as MuiTooltip, styled } from '@mui/material';
import type { TooltipProps as MuiTooltipProps } from '@mui/material/Tooltip';

interface TooltipProps extends Omit<MuiTooltipProps, 'title'> {
  title: string;
  children: React.ReactElement;
  variant?: 'default' | 'dark' | 'light';
  size?: 'small' | 'medium' | 'large';
  arrow?: boolean;
  placement?: MuiTooltipProps['placement'];
  delay?: number;
}

// Export the interface for external use
export type { TooltipProps };

// Styled tooltip with custom variants
const StyledTooltip = styled(MuiTooltip)<{ variant?: string; size?: string }>(
  ({ theme, variant, size }) => ({
    '& .MuiTooltip-tooltip': {
      backgroundColor:
        variant === 'light'
          ? theme.palette.background.paper
          : variant === 'dark'
            ? theme.palette.grey[900]
            : theme.palette.grey[700],
      color: variant === 'light' ? theme.palette.text.primary : theme.palette.common.white,
      fontSize: size === 'small' ? '0.625rem' : size === 'large' ? '0.875rem' : '0.75rem',
      padding:
        size === 'small'
          ? theme.spacing(0.5, 1)
          : size === 'large'
            ? theme.spacing(1.5, 2)
            : theme.spacing(1, 1.5),
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[4],
      border: variant === 'light' ? `1px solid ${theme.palette.divider}` : 'none',
    },
    '& .MuiTooltip-arrow': {
      color:
        variant === 'light'
          ? theme.palette.background.paper
          : variant === 'dark'
            ? theme.palette.grey[900]
            : theme.palette.grey[700],
    },
  })
);

export const Tooltip: React.FC<TooltipProps> = ({
  title,
  children,
  variant = 'default',
  size = 'medium',
  arrow = true,
  placement = 'right',
  delay = 500,
  ...props
}) => {
  return (
    <StyledTooltip
      title={title}
      variant={variant}
      size={size}
      arrow={arrow}
      placement={placement}
      enterDelay={delay}
      enterNextDelay={delay}
      leaveDelay={200}
      {...props}
    >
      {children}
    </StyledTooltip>
  );
};
